import { useEffect, useId, useState } from "react";
import { useIntl } from "react-intl";
import { useHydrated } from "remix-utils/use-hydrated";

import { useField, useFormContext } from "@rvf/react-router";

import {
  FieldError,
  Form,
  InputField,
  SelectField,
  TextareaField,
} from "~/components/forms/Form";
import { LoadingButton } from "~/components/ui/loading-button";
import { ClaimResponseMessage } from "~/hec/components/molecules/claim-response-message/claim-response-message";
import {
  CONTACT_US_ENDPOINT,
  liveEmailError,
} from "~/hec/contact-us/contact-us.logic";
import {
  CONTACT_US_MAX_LENGTH,
  CONTACT_US_SUBJECTS,
  contactUsValidator,
} from "~/hec/contact-us/contact-us.schemas";
import type { ContactUsStatus } from "~/hec/contact-us/types";
import { useContactUsForm } from "~/hec/contact-us/use-contact-us-form";
import { cn } from "~/lib/ui";

import { resolveContactUsFormCopy } from "./copy";
import messages from "./messages";
import type {
  ContactUsEditorState,
  ContactUsEmailFieldProps,
  ContactUsFormProps,
} from "./types";

/**
 * Organism: the Contact Us form (COM-735) — the React port of the SFRA
 * content asset `form.c-contactus` + `contactUs.js`. Validation is RVF over the
 * shared `contactUsValidator`; `useContactUsForm` owns the send.
 *
 * Validation timing is the ticket's, not RVF's default: the whole form checks
 * on Submit only, and Email alone checks its FORMAT live (`ContactUsEmailField`).
 *
 * Behaviour deltas vs SFRA, all deliberate:
 *  - Order Number is always required (AC);
 *  - the button disables while sending, so a double click sends one email (AC);
 *  - a failure message stays until the next attempt (SFRA hid it after 5s);
 *  - Submit is disabled until hydration, so a pre-hydration click cannot fall
 *    through to a native form POST.
 */
const SUBMIT_ONLY = {
  initial: "onSubmit",
  whenTouched: "onSubmit",
  whenSubmitted: "onSubmit",
} as const;

const EDITOR_STATUS: Record<ContactUsEditorState, ContactUsStatus> = {
  ready: { kind: "idle" },
  errors: { kind: "idle" },
  submitting: { kind: "submitting" },
  success: { kind: "succeeded" },
  failure: { kind: "failed", reason: "unavailable" },
};

/**
 * Editor "errors" preview: validates the empty form once on mount, so every
 * field shows its real Submit-time copy — the schema's, not a sample. Rendered
 * only inside the Builder editor.
 */
function EditorErrorPreview() {
  const form = useFormContext();
  useEffect(() => {
    void form.validate();
    // Mount-only: the form is re-keyed when the preview state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/**
 * The live check renders its OWN message rather than writing RVF's field error.
 * Two RVF mechanics rule that out: `validateField` also publishes errors for
 * every other TOUCHED field (an empty Name the shopper tabbed past would light
 * up — the AC forbids that), and under a Submit-only form RVF's own `onChange`
 * runs after the caller's and clears the field error. RVF's Submit-time error
 * still renders through `FormMessage`; the live one steps aside when it does.
 */
function ContactUsEmailField({ label, displayName }: ContactUsEmailFieldProps) {
  const field = useField("email");
  const messageId = useId();
  // Every keystroke, from the first (AC: "format checking as the user types").
  // This also takes over after a Submit-time email error: RVF clears its own
  // error on the next change, and this one is already tracking the value.
  const [liveError, setLiveError] = useState<string | null>(null);
  const shown = field.error() ? null : liveError;

  return (
    <div>
      <InputField
        name="email"
        type="email"
        label={label}
        displayName={displayName}
        autoComplete="email"
        maxLength={CONTACT_US_MAX_LENGTH.email}
        onChange={e => setLiveError(liveEmailError(e.target.value))}
        // Spread only when set: `Slot` lets a child's `undefined` override
        // `FormControl`'s own aria wiring.
        {...(shown
          ? { "aria-invalid": true, "aria-describedby": messageId }
          : {})}
        data-testid="contact-us-email-input"
      />
      {shown && (
        <FieldError
          id={messageId}
          // Same offset as `FormMessage`, so the message doesn't jump when the
          // Submit-time error takes over from it.
          className="px-2 pt-2 text-right"
          data-testid="contact-us-email-live-error"
        >
          {shown}
        </FieldError>
      )}
    </div>
  );
}

export function ContactUsForm({
  copy,
  editorState,
  className,
  ...rest
}: ContactUsFormProps) {
  const intl = useIntl();
  const hydrated = useHydrated();
  const inEditor = editorState !== undefined;
  const contactUs = useContactUsForm({ enabled: !inEditor });
  const status = inEditor ? EDITOR_STATUS[editorState] : contactUs.status;
  const text = resolveContactUsFormCopy(copy);
  const submitting = status.kind === "submitting";

  const label = (descriptor: (typeof messages)[keyof typeof messages]) => {
    const value = intl.formatMessage(descriptor);
    return { label: value.toUpperCase(), displayName: value };
  };

  const subjectOptions = [
    { value: "", label: intl.formatMessage(messages.subjectPlaceholder) },
    ...CONTACT_US_SUBJECTS.map(subject => ({ value: subject, label: subject })),
  ];

  return (
    <div
      className={cn("w-full font-grotesque", className)}
      data-testid="contact-us-form"
      {...rest}
    >
      <Form
        // Remounted after a successful send: clears every field AND re-sinks
        // the floating labels (a native reset leaves them floated). The editor
        // state is in the key so its error preview re-seeds.
        key={`${contactUs.formKey}:${editorState ?? "live"}`}
        validator={contactUsValidator}
        // Only reached before hydration (and Submit is disabled then): a native
        // submit must POST to the action, never GET the page with the
        // shopper's details in the query string.
        method="post"
        action={CONTACT_US_ENDPOINT}
        validationBehaviorConfig={SUBMIT_ONLY}
        handleSubmit={contactUs.submit}
        trackingFormName="contactUs"
        noValidate
        className="flex flex-col gap-2"
        data-testid="contact-us-form-root"
      >
        {editorState === "errors" && <EditorErrorPreview />}
        <InputField
          name="name"
          {...label(messages.nameLabel)}
          autoComplete="name"
          maxLength={CONTACT_US_MAX_LENGTH.name}
          data-testid="contact-us-name-input"
        />
        <ContactUsEmailField {...label(messages.emailLabel)} />
        <SelectField
          name="subject"
          {...label(messages.subjectLabel)}
          options={subjectOptions}
          // "Please Select" is a real, visible option (value ""), as on SFRA,
          // so the label sits floated above it from first paint.
          blankOption={false}
          defaultValue=""
          wrapperProps={{ alwaysFloated: true }}
          data-testid="contact-us-subject-select"
        />
        <InputField
          name="orderNumber"
          {...label(messages.orderNumberLabel)}
          autoComplete="off"
          maxLength={CONTACT_US_MAX_LENGTH.orderNumber}
          data-testid="contact-us-order-number-input"
        />
        <TextareaField
          name="description"
          {...label(messages.descriptionLabel)}
          data-testid="contact-us-description-input"
        />

        <div className="mt-4">
          {/* `LoadingButton`, not `TriButton`: the button remounts with the
              form after a send, so a "done" morph could never render. The
              success line below replaces SFRA's 5s "Submitted" label. */}
          <LoadingButton
            type="submit"
            variant="cta"
            size="fit"
            // SFRA `.btn-primary.btn-block`: 50px, 16px, 8px radius (measured
            // on fsa.devhec.com). Before hydration the button is inert but
            // keeps its live colours, so SSR never flashes a greyed-out form.
            className={cn(
              "h-[3.125rem] w-full rounded-lg px-9 text-base",
              !hydrated &&
                "disabled:border-neutral-12 disabled:bg-primary disabled:text-neutral-1",
            )}
            disabled={!hydrated || submitting}
            isLoading={submitting}
            data-testid="contact-us-submit-button"
          >
            {text.submitLabel}
          </LoadingButton>
          {status.kind === "succeeded" && (
            <ClaimResponseMessage
              tone="success"
              format="text"
              message={text.successMessage}
              className="mt-2 text-sm text-success-11"
              data-testid="contact-us-response-message"
            />
          )}
          {status.kind === "failed" && (
            <ClaimResponseMessage
              tone="error"
              format="text"
              message={
                status.reason === "rate_limited"
                  ? intl.formatMessage(messages.rateLimited)
                  : text.failureMessage
              }
              className="mt-2 text-sm text-error"
              data-testid="contact-us-response-message"
            />
          )}
        </div>
      </Form>
    </div>
  );
}
